import React, { useState } from 'react';
import { submitBusiness, getDemoHtmlUrl, emailDemos } from '../api';

/* ═══════════════════════════════════════════
   GENERATING LOADER — Cinematic Ritual Screen
   ═══════════════════════════════════════════ */
const GeneratingLoader = () => {
  const [dots, setDots] = useState('');
  const [tipIndex, setTipIndex] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  const tips = [
    '🏰 Se construiesc zidurile digitale...',
    '🎨 Vampirul alege paletele de culori...',
    '📱 Se verifică responsive design-ul...',
    '✨ Se adaugă animații CSS magice...',
    '🧄 AI-ul din Transilvania lucrează...',
    '📐 Se optimizează layout-ul...',
    '🖼️ Se aranjează secțiunile...',
    '🔮 Aproape gata! Încă puțin...',
  ];

  React.useEffect(() => {
    const dotTimer = setInterval(() => setDots(d => d.length >= 3 ? '' : d + '.'), 500);
    const tipTimer = setInterval(() => setTipIndex(i => (i + 1) % tips.length), 3500);
    const elapsedTimer = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => { clearInterval(dotTimer); clearInterval(tipTimer); clearInterval(elapsedTimer); };
  }, []);

  // Generate particles
  const particles = React.useMemo(() =>
    [...Array(20)].map((_, i) => ({
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 1 + Math.random() * 2,
      dur: 4 + Math.random() * 6,
      delay: -Math.random() * 8,
      opacity: 0.1 + Math.random() * 0.3,
    })), []);

  // Rune symbols for the magic circle
  const runes = ['ᚠ', 'ᚢ', 'ᚦ', 'ᚨ', 'ᚱ', 'ᚲ', 'ᚷ', 'ᚹ', 'ᚺ', 'ᚾ', 'ᛁ', 'ᛃ'];

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      background: '#0a0b0f',
      display: 'flex', flexDirection: 'column',
      justifyContent: 'center', alignItems: 'center', padding: 24,
      overflow: 'hidden',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

        @keyframes outerRing {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(360deg); }
        }
        @keyframes innerRing {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(-360deg); }
        }
        @keyframes vampFloat {
          0%, 100% { transform: translateY(0); filter: drop-shadow(0 0 30px rgba(189,147,249,0.4)); }
          50% { transform: translateY(-10px); filter: drop-shadow(0 0 50px rgba(255,121,198,0.5)); }
        }
        @keyframes glowPulse {
          0%, 100% { opacity: 0.15; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 0.3; transform: translate(-50%, -50%) scale(1.1); }
        }
        @keyframes particleDrift {
          0%, 100% { transform: translate(0, 0); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          50% { transform: translate(var(--dx), var(--dy)); }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes cursorBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
        @keyframes tipFade {
          0% { opacity: 0; transform: translateY(8px); }
          15% { opacity: 1; transform: translateY(0); }
          85% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-8px); }
        }
        @keyframes runeGlow {
          0%, 100% { text-shadow: 0 0 8px rgba(189,147,249,0.4); }
          50% { text-shadow: 0 0 16px rgba(189,147,249,0.8); }
        }
      `}</style>

      {/* Background radial glow */}
      <div style={{
        position: 'absolute', top: '40%', left: '50%',
        width: 500, height: 500, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(189,147,249,0.08) 0%, rgba(255,121,198,0.04) 40%, transparent 70%)',
        animation: 'glowPulse 4s ease-in-out infinite',
        pointerEvents: 'none',
      }} />

      {/* Floating particles */}
      {particles.map((p, i) => (
        <div key={i} style={{
          position: 'absolute',
          left: `${p.x}%`, top: `${p.y}%`,
          width: p.size, height: p.size,
          borderRadius: '50%',
          background: i % 3 === 0 ? '#bd93f9' : i % 3 === 1 ? '#ff79c6' : '#50fa7b',
          opacity: p.opacity,
          animation: `particleDrift ${p.dur}s ease-in-out ${p.delay}s infinite`,
          '--dx': `${(Math.random() - 0.5) * 80}px`,
          '--dy': `${(Math.random() - 0.5) * 80}px`,
          pointerEvents: 'none',
        }} />
      ))}

      {/* Magic circle container */}
      <div style={{ position: 'relative', width: 200, height: 200, marginBottom: 36 }}>
        {/* Outer rotating ring with runes */}
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          width: 190, height: 190,
          border: '1px solid rgba(189,147,249,0.15)',
          borderRadius: '50%',
          animation: 'outerRing 20s linear infinite',
        }}>
          {runes.map((r, i) => {
            const angle = (360 / runes.length) * i;
            const rad = (angle * Math.PI) / 180;
            const radius = 88;
            return (
              <span key={i} style={{
                position: 'absolute',
                left: `${50 + (Math.cos(rad) * radius / 95) * 50}%`,
                top: `${50 + (Math.sin(rad) * radius / 95) * 50}%`,
                transform: 'translate(-50%, -50%)',
                color: '#bd93f9',
                fontSize: 13, opacity: 0.5,
                animation: `runeGlow 3s ease-in-out ${i * 0.25}s infinite`,
              }}>{r}</span>
            );
          })}
        </div>

        {/* Inner rotating ring */}
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          width: 140, height: 140,
          border: '1px dashed rgba(255,121,198,0.12)',
          borderRadius: '50%',
          animation: 'innerRing 15s linear infinite',
        }} />

        {/* Center: Vampire emoji */}
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          fontSize: 64,
          animation: 'vampFloat 3s ease-in-out infinite',
          zIndex: 2,
        }}>🧛</div>
      </div>

      {/* Title */}
      <h2 style={{
        fontFamily: "'Space Grotesk', sans-serif",
        color: '#f8f8f2', fontSize: 24, marginBottom: 10,
        textAlign: 'center', fontWeight: 700,
        letterSpacing: '-0.01em',
      }}>
        Ritualul digital a început{dots}
      </h2>

      {/* Subtitle */}
      <p style={{
        color: '#6272a4', fontSize: 13, textAlign: 'center',
        maxWidth: 380, lineHeight: 1.6, marginBottom: 28,
        fontFamily: "'Space Grotesk', sans-serif",
      }}>
        Generăm 3 variante de website personalizate. Magia durează 15-30 de secunde.
      </p>

      {/* Rotating tips */}
      <div style={{
        height: 24, marginBottom: 28, overflow: 'hidden',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div key={tipIndex} style={{
          color: '#8be9fd', fontSize: 13, textAlign: 'center',
          fontFamily: "'Space Grotesk', sans-serif", fontWeight: 500,
          animation: 'tipFade 3.5s ease-in-out',
        }}>
          {tips[tipIndex]}
        </div>
      </div>

      {/* Progress bar */}
      <div style={{
        width: 300, height: 3, background: '#1a1c28',
        borderRadius: 2, overflow: 'hidden', marginBottom: 20,
      }}>
        <div style={{
          height: '100%', width: '100%',
          background: 'linear-gradient(90deg, #bd93f9, #ff79c6, #50fa7b, #8be9fd, #bd93f9)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.8s linear infinite',
          borderRadius: 2,
        }} />
      </div>

      {/* Terminal line + elapsed time */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 16,
      }}>
        <div style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 11, color: '#50fa7b',
          display: 'flex', alignItems: 'center', gap: 5,
        }}>
          <span style={{ animation: 'cursorBlink 1s step-end infinite' }}>▊</span>
          generating demos...
        </div>
        <div style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 10, color: '#44475a',
        }}>
          {elapsed}s
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   DEMO PREVIEW CARD — Browser Window Style
   ═══════════════════════════════════════════ */
const DemoCard = ({ demo, submissionId, index, onFullscreen }) => {
  const [hovered, setHovered] = useState(false);
  const iframeUrl = `${process.env.REACT_APP_API_URL || ""}/api/demos/${submissionId}/${demo.variant_number}/`;
  const colors = ['#50fa7b', '#ff79c6', '#bd93f9'];
  const styleLabels = ['Modern & Minimalist', 'Bold & Colorful', 'Classic & Professional'];
  const icons = ['🎨', '🎆', '🏛️'];
  const color = colors[index] || '#bd93f9';
  const fileName = `demo-v${demo.variant_number}-${(submissionId || '').slice(0, 8)}.html`;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: '#111218',
        border: `1px solid ${hovered ? color + '40' : '#1e2030'}`,
        borderRadius: 12, overflow: 'hidden',
        transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
        transform: hovered ? 'translateY(-8px) scale(1.01)' : 'none',
        boxShadow: hovered ? `0 20px 50px ${color}18, 0 0 0 1px ${color}15` : '0 4px 20px rgba(0,0,0,0.3)',
      }}>

      {/* ── Browser Chrome ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 14px',
        background: '#0d0e12',
        borderBottom: '1px solid #1a1c28',
      }}>
        {/* Traffic lights */}
        <div style={{ display: 'flex', gap: 5, marginRight: 8 }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ff5555' }} />
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#f1fa8c' }} />
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#50fa7b' }} />
        </div>

        {/* URL bar */}
        <div style={{
          flex: 1, background: '#16181e', borderRadius: 6,
          padding: '5px 12px', display: 'flex', alignItems: 'center', gap: 6,
          border: '1px solid #1e2030',
        }}>
          <span style={{ fontSize: 9, color: '#44475a' }}>🔒</span>
          <span style={{
            fontSize: 10, color: '#6272a4',
            fontFamily: "'JetBrains Mono', monospace",
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{fileName}</span>
        </div>

        {/* Variant badge */}
        <div style={{
          background: `${color}18`, border: `1px solid ${color}30`,
          borderRadius: 6, padding: '3px 10px',
          fontSize: 10, fontWeight: 700, color: color,
          fontFamily: "'JetBrains Mono', monospace",
          letterSpacing: 0.5, flexShrink: 0,
        }}>V{demo.variant_number}</div>
      </div>

      {/* ── Website Preview ── */}
      <div style={{
        height: 320, background: '#fff',
        position: 'relative', overflow: 'hidden',
        cursor: 'pointer',
      }}
      onClick={() => onFullscreen(demo)}>
        <iframe
          src={iframeUrl}
          title={demo.title}
          style={{
            width: '250%', height: '250%',
            transform: 'scale(0.4)',
            transformOrigin: 'top left',
            border: 'none',
            pointerEvents: 'none',
          }}
          sandbox="allow-scripts allow-same-origin"
        />

        {/* Hover overlay */}
        <div style={{
          position: 'absolute', inset: 0,
          background: `linear-gradient(180deg, transparent 60%, rgba(13,14,18,0.9) 100%)`,
          opacity: hovered ? 1 : 0.4,
          transition: 'opacity 0.3s',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
          padding: '0 0 16px',
        }}>
          <div style={{
            background: `${color}dd`, borderRadius: 8,
            padding: '8px 24px',
            fontSize: 12, fontWeight: 700, color: '#0d0e12',
            fontFamily: "'Space Grotesk', sans-serif",
            transform: hovered ? 'translateY(0)' : 'translateY(10px)',
            opacity: hovered ? 1 : 0,
            transition: 'all 0.3s',
            boxShadow: `0 4px 20px ${color}40`,
          }}>
            🔍 Click pentru Full Screen
          </div>
        </div>
      </div>

      {/* ── Info Bar ── */}
      <div style={{
        padding: '14px 16px',
        borderTop: '1px solid #1a1c28',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div>
          <div style={{
            fontSize: 13, fontWeight: 600, color: '#f8f8f2', marginBottom: 2,
            fontFamily: "'Space Grotesk', sans-serif",
          }}>
            {icons[index]} {styleLabels[index] || demo.title}
          </div>
          <div style={{
            fontSize: 10, color: '#44475a',
            fontFamily: "'JetBrains Mono', monospace",
          }}>
            {demo.description ? demo.description.slice(0, 50) + '...' : 'Website demo generat cu AI'}
          </div>
        </div>
        <button
          onClick={() => onFullscreen(demo)}
          style={{
            background: 'transparent', border: `1px solid ${color}30`,
            borderRadius: 6, padding: '6px 12px',
            color: color, fontSize: 11, fontWeight: 600, cursor: 'pointer',
            fontFamily: "'JetBrains Mono', monospace",
            transition: 'all 0.2s', flexShrink: 0,
          }}
          onMouseEnter={e => { e.target.style.background = `${color}15`; e.target.style.borderColor = color; }}
          onMouseLeave={e => { e.target.style.background = 'transparent'; e.target.style.borderColor = `${color}30`; }}>
          Deschide →
        </button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   FULLSCREEN DEMO PREVIEW
   ═══════════════════════════════════════════ */
const FullscreenPreview = ({ demo, submissionId, onClose }) => {
  const iframeUrl = `${process.env.REACT_APP_API_URL || ""}/api/demos/${submissionId}/${demo.variant_number}/`;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 300,
      background: '#000', display: 'flex', flexDirection: 'column',
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '10px 20px', background: '#0d0e12',
        borderBottom: '1px solid #1e2030',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 18 }}>🧛</span>
          <span style={{
            color: '#f8f8f2', fontWeight: 600, fontSize: 14,
            fontFamily: "'Space Grotesk', sans-serif",
          }}>{demo.title}</span>
          <span style={{
            background: '#50fa7b15', color: '#50fa7b',
            padding: '2px 10px', borderRadius: 10, fontSize: 10, fontWeight: 600,
            fontFamily: "'JetBrains Mono', monospace",
          }}>V{demo.variant_number}</span>
        </div>
        <button onClick={onClose} style={{
          background: '#ff555520', border: '1px solid #ff555540', borderRadius: 8,
          padding: '6px 16px', color: '#ff5555', fontWeight: 600,
          cursor: 'pointer', fontSize: 12, fontFamily: "'Space Grotesk', sans-serif",
          transition: 'all 0.2s',
        }}
        onMouseEnter={e => { e.target.style.background = '#ff5555'; e.target.style.color = '#fff'; }}
        onMouseLeave={e => { e.target.style.background = '#ff555520'; e.target.style.color = '#ff5555'; }}>
          ✕ Închide
        </button>
      </div>
      <iframe src={iframeUrl} title={demo.title} style={{ flex: 1, border: 'none', width: '100%' }} />
    </div>
  );
};

/* ═══════════════════════════════════════════
   DEMOS RESULT PAGE — Premium Showcase
   ═══════════════════════════════════════════ */
const DemosResult = ({ submission, onClose }) => {
  const [fullscreenDemo, setFullscreenDemo] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailError, setEmailError] = useState('');

  const handleSendEmail = async () => {
    setEmailSending(true);
    setEmailError('');
    try {
      await emailDemos(submission.id);
      setEmailSent(true);
    } catch (err) {
      setEmailError('Eroare la trimiterea email-ului. Încearcă din nou.');
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      background: '#0a0b0f',
      overflowY: 'auto',
    }}>
      <style>{`
        @keyframes confettiFade {
          0% { opacity: 1; transform: translateY(0) rotate(0deg); }
          100% { opacity: 0; transform: translateY(60px) rotate(180deg); }
        }
        @keyframes resultSlide {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '60px 24px 40px' }}>

        {/* ── Header ── */}
        <div style={{
          textAlign: 'center', marginBottom: 56,
          animation: 'resultSlide 0.6s ease-out',
        }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: '#50fa7b12', border: '1px solid #50fa7b25',
            borderRadius: 20, padding: '6px 18px', marginBottom: 20,
          }}>
            <span style={{ fontSize: 14 }}>✓</span>
            <span style={{
              fontSize: 11, fontWeight: 600, color: '#50fa7b',
              fontFamily: "'JetBrains Mono', monospace", letterSpacing: 0.5,
            }}>GENERAT CU SUCCES</span>
          </div>

          <h2 style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: 'clamp(24px, 4vw, 34px)', color: '#f8f8f2',
            marginBottom: 10, fontWeight: 700, letterSpacing: '-0.01em',
          }}>
            🏰 Cele 3 variante demo sunt gata!
          </h2>
          <p style={{
            color: '#6272a4', fontSize: 15,
            fontFamily: "'Space Grotesk', sans-serif",
          }}>
            <span style={{ color: '#8be9fd' }}>{submission.business_name || submission.name}</span> — alege varianta preferată
          </p>
        </div>

        {/* ── Demo Cards Grid ── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 24, marginBottom: 56,
        }}>
          {(submission.demos || []).map((demo, i) => (
            <div key={demo.id} style={{ animation: `resultSlide 0.6s ease-out ${0.15 * i}s both` }}>
              <DemoCard
                demo={demo}
                submissionId={submission.id}
                index={i}
                onFullscreen={setFullscreenDemo}
              />
            </div>
          ))}
        </div>

        {/* ── Email Section ── */}
        <div style={{
          background: '#111218', border: '1px solid #1e2030',
          borderRadius: 16, padding: '36px 32px',
          textAlign: 'center', maxWidth: 500, margin: '0 auto 32px',
          animation: 'resultSlide 0.6s ease-out 0.5s both',
        }}>
          {emailSent ? (
            <>
              <div style={{
                width: 48, height: 48, borderRadius: '50%',
                background: '#50fa7b15', border: '2px solid #50fa7b30',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px', fontSize: 22,
              }}>✓</div>
              <h3 style={{
                color: '#50fa7b', fontFamily: "'Space Grotesk', sans-serif",
                marginBottom: 8, fontSize: 18, fontWeight: 700,
              }}>Demo-urile au fost trimise!</h3>
              <p style={{ color: '#6272a4', fontSize: 13, fontFamily: "'Space Grotesk', sans-serif" }}>
                Verifică inbox-ul la <span style={{ color: '#f1fa8c' }}>{submission.email}</span>
              </p>
            </>
          ) : (
            <>
              <div style={{ fontSize: 32, marginBottom: 14 }}>📧</div>
              <h3 style={{
                color: '#f8f8f2', fontFamily: "'Space Grotesk', sans-serif",
                fontSize: 17, marginBottom: 8, fontWeight: 600,
              }}>Vrei să primești demo-urile pe email?</h3>
              <p style={{ color: '#6272a4', fontSize: 12, marginBottom: 20, fontFamily: "'Space Grotesk', sans-serif" }}>
                Le trimitem la <span style={{ color: '#f1fa8c' }}>{submission.email}</span> ca fișiere HTML atașate.
              </p>
              {emailError && (
                <p style={{ color: '#ff5555', fontSize: 11, marginBottom: 12 }}>{emailError}</p>
              )}
              <button onClick={handleSendEmail} disabled={emailSending} style={{
                background: emailSending ? '#1e2030' : 'linear-gradient(135deg, #ff79c6, #bd93f9)',
                border: 'none', borderRadius: 10, padding: '12px 32px',
                color: '#fff', fontWeight: 700, fontSize: 13,
                cursor: emailSending ? 'wait' : 'pointer',
                fontFamily: "'Space Grotesk', sans-serif",
                transition: 'all 0.2s',
              }}>
                {emailSending ? '📧 Se trimite...' : '📧 Trimite-mi pe email'}
              </button>
            </>
          )}
        </div>

        {/* ── CTA: Transform to Real Site ── */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(189,147,249,0.06), rgba(80,250,123,0.04))',
          border: '1px solid rgba(189,147,249,0.12)',
          borderRadius: 16, padding: '32px 28px',
          textAlign: 'center', maxWidth: 500, margin: '0 auto 32px',
          animation: 'resultSlide 0.6s ease-out 0.6s both',
        }}>
          <h3 style={{
            color: '#f8f8f2', fontFamily: "'Space Grotesk', sans-serif",
            fontSize: 17, marginBottom: 8, fontWeight: 600,
          }}>Îți place o variantă?</h3>
          <p style={{
            color: '#6272a4', fontSize: 12, marginBottom: 20, lineHeight: 1.6,
            fontFamily: "'Space Grotesk', sans-serif",
          }}>
            Hai să o transformăm în site-ul tău real. Prețuri de la 500 RON, livrare în 2-4 săptămâni.
          </p>
          <a href="mailto:contact@draculadev.com?subject=Vreau%20site%20-%20DraculaDev" style={{
            display: 'inline-block',
            background: 'linear-gradient(135deg, #50fa7b, #8be9fd)',
            border: 'none', borderRadius: 10, padding: '12px 28px',
            color: '#0d0e12', fontWeight: 700, fontSize: 13,
            fontFamily: "'Space Grotesk', sans-serif",
            textDecoration: 'none',
            transition: 'all 0.2s',
          }}>
            💬 Contactează-ne
          </a>
        </div>

        {/* ── Close ── */}
        <div style={{ textAlign: 'center', animation: 'resultSlide 0.6s ease-out 0.7s both' }}>
          <button onClick={onClose} style={{
            background: 'transparent', border: '1px solid #1e2030',
            borderRadius: 8, padding: '10px 24px',
            color: '#44475a', cursor: 'pointer',
            fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
            transition: 'all 0.2s',
          }}
          onMouseEnter={e => { e.target.style.borderColor = '#44475a'; e.target.style.color = '#f8f8f2'; }}
          onMouseLeave={e => { e.target.style.borderColor = '#1e2030'; e.target.style.color = '#44475a'; }}>
            Închide
          </button>
        </div>
      </div>

      {fullscreenDemo && (
        <FullscreenPreview
          demo={fullscreenDemo}
          submissionId={submission.id}
          onClose={() => setFullscreenDemo(null)}
        />
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════
   MAIN BUSINESS FORM
   ═══════════════════════════════════════════ */
const BusinessForm = ({ onClose }) => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', businessName: '',
    businessType: '', description: '', hasWebsite: 'nu',
    currentWebsite: '', wantDemo: true,
  });
  const [step, setStep] = useState('form');
  const [submission, setSubmission] = useState(null);
  const [error, setError] = useState('');
  const [focusedField, setFocusedField] = useState(null);

  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !formData.description) {
      setError('Completează câmpurile obligatorii (Nume, Email, Descriere).');
      return;
    }
    if (formData.description.length < 20) {
      setError('Descrierea trebuie să aibă cel puțin 20 de caractere.');
      return;
    }

    setError('');
    setStep('generating');

    try {
      const result = await submitBusiness(formData);
      setSubmission(result);
      if (result.demos && result.demos.length > 0) {
        setStep('demos');
      } else {
        setStep('form');
        setError('Demo-urile nu au putut fi generate. Verifică configurarea API-ului.');
      }
    } catch (err) {
      setStep('form');
      const msg = err.response?.data;
      if (msg && typeof msg === 'object') {
        const firstError = Object.values(msg).flat()[0];
        setError(typeof firstError === 'string' ? firstError : 'Eroare la trimitere.');
      } else {
        setError('Eroare la trimitere. Încearcă din nou.');
      }
    }
  };

  if (step === 'generating') return <GeneratingLoader />;
  if (step === 'demos' && submission) return <DemosResult submission={submission} onClose={onClose} />;

  const inputStyle = (field) => ({
    width: '100%', padding: '13px 16px',
    background: '#0d0e12',
    border: `1px solid ${focusedField === field ? '#bd93f9' : '#1e2030'}`,
    borderRadius: 10, color: '#f8f8f2',
    fontFamily: "'JetBrains Mono', monospace", fontSize: 13,
    outline: 'none', boxSizing: 'border-box',
    transition: 'all 0.2s ease',
    boxShadow: focusedField === field ? '0 0 0 3px rgba(189,147,249,0.1)' : 'none',
  });

  const labelStyle = {
    display: 'block', color: '#8be9fd', fontSize: 10,
    marginBottom: 6, fontWeight: 600, letterSpacing: 1,
    textTransform: 'uppercase',
    fontFamily: "'JetBrains Mono', monospace",
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      background: 'rgba(10,11,15,0.9)', backdropFilter: 'blur(16px)',
      display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
      padding: 24, overflowY: 'auto',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
      `}</style>

      <div style={{
        background: '#16181e', borderRadius: 18, padding: '36px 32px',
        maxWidth: 560, width: '100%',
        border: '1px solid #1e2030',
        boxShadow: '0 24px 80px rgba(0,0,0,0.6), 0 0 60px rgba(189,147,249,0.05)',
        margin: '60px 0 40px',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-start', marginBottom: 28,
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <span style={{ fontSize: 28 }}>🧛</span>
              <h2 style={{
                fontFamily: "'Space Grotesk', sans-serif",
                color: '#f8f8f2', fontSize: 22, fontWeight: 700,
                margin: 0,
              }}>Descrie afacerea ta</h2>
            </div>
            <p style={{
              color: '#6272a4', fontSize: 12, margin: 0,
              fontFamily: "'Space Grotesk', sans-serif",
            }}>
              🧄 Ai-ul nostru generează instant 3 variante demo personalizate
            </p>
          </div>
          <button onClick={onClose} style={{
            background: '#ff555510', border: '1px solid #ff555520',
            borderRadius: 8, width: 32, height: 32,
            color: '#ff5555', fontSize: 14, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.2s', flexShrink: 0,
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#ff5555'; e.currentTarget.style.color = '#fff'; }}
          onMouseLeave={e => { e.currentTarget.style.background = '#ff555510'; e.currentTarget.style.color = '#ff5555'; }}>
            ✕
          </button>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            background: '#ff555510', border: '1px solid #ff555530',
            borderRadius: 10, padding: '10px 16px', marginBottom: 20,
            color: '#ff5555', fontSize: 12,
            fontFamily: "'Space Grotesk', sans-serif",
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span>⚠️</span> {error}
          </div>
        )}

        {/* Form */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* Row 1: Nume + Email */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label style={labelStyle}>Nume *</label>
              <input
                style={inputStyle('name')}
                placeholder="Ion Popescu"
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                onFocus={() => setFocusedField('name')}
                onBlur={() => setFocusedField(null)}
              />
            </div>
            <div>
              <label style={labelStyle}>Email *</label>
              <input
                style={inputStyle('email')}
                placeholder="email@exemplu.ro"
                type="email"
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
                onFocus={() => setFocusedField('email')}
                onBlur={() => setFocusedField(null)}
              />
            </div>
          </div>

          {/* Row 2: Telefon + Numele afacerii */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label style={labelStyle}>Telefon</label>
              <input
                style={inputStyle('phone')}
                placeholder="07xx xxx xxx"
                value={formData.phone}
                onChange={e => setFormData({ ...formData, phone: e.target.value })}
                onFocus={() => setFocusedField('phone')}
                onBlur={() => setFocusedField(null)}
              />
            </div>
            <div>
              <label style={labelStyle}>Numele afacerii</label>
              <input
                style={inputStyle('businessName')}
                placeholder="Firma mea SRL"
                value={formData.businessName}
                onChange={e => setFormData({ ...formData, businessName: e.target.value })}
                onFocus={() => setFocusedField('businessName')}
                onBlur={() => setFocusedField(null)}
              />
            </div>
          </div>

          {/* Row 3: Tipul afacerii */}
          <div>
            <label style={labelStyle}>Tipul afacerii</label>
            <select
              style={{ ...inputStyle('type'), cursor: 'pointer', appearance: 'none' }}
              value={formData.businessType}
              onChange={e => setFormData({ ...formData, businessType: e.target.value })}
              onFocus={() => setFocusedField('type')}
              onBlur={() => setFocusedField(null)}>
              <option value="">Selectează...</option>
              <option value="restaurant">Restaurant / Cafenea</option>
              <option value="magazin">Magazin / Retail</option>
              <option value="servicii">Servicii profesionale</option>
              <option value="sanatate">Sănătate / Fitness</option>
              <option value="constructii">Construcții / Renovări</option>
              <option value="educatie">Educație / Cursuri</option>
              <option value="turism">Turism / Cazare</option>
              <option value="auto">Auto / Service</option>
              <option value="beauty">Salon / Beauty</option>
              <option value="altele">Altele</option>
            </select>
          </div>

          {/* Row 4: Are website */}
          <div>
            <label style={labelStyle}>Ai deja un website?</label>
            <div style={{ display: 'flex', gap: 10 }}>
              {['nu', 'da'].map(opt => (
                <button key={opt}
                  onClick={() => setFormData({ ...formData, hasWebsite: opt })}
                  style={{
                    padding: '9px 28px', borderRadius: 8,
                    border: `1px solid ${formData.hasWebsite === opt ? '#bd93f9' : '#1e2030'}`,
                    background: formData.hasWebsite === opt ? '#bd93f915' : '#0d0e12',
                    color: formData.hasWebsite === opt ? '#bd93f9' : '#6272a4',
                    cursor: 'pointer',
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 13, fontWeight: 500,
                    transition: 'all 0.2s',
                  }}>
                  {opt === 'da' ? 'Da' : 'Nu'}
                </button>
              ))}
            </div>
          </div>

          {/* Conditional: URL website */}
          {formData.hasWebsite === 'da' && (
            <div>
              <label style={labelStyle}>Link website actual</label>
              <input
                style={inputStyle('website')}
                placeholder="https://..."
                value={formData.currentWebsite}
                onChange={e => setFormData({ ...formData, currentWebsite: e.target.value })}
                onFocus={() => setFocusedField('website')}
                onBlur={() => setFocusedField(null)}
              />
            </div>
          )}

          {/* Row 5: Descriere */}
          <div>
            <label style={labelStyle}>Descrie afacerea ta *</label>
            <textarea
              style={{
                ...inputStyle('desc'),
                minHeight: 120, resize: 'vertical', lineHeight: 1.7,
              }}
              placeholder="Ce vinzi sau ce servicii oferi? Cine sunt clienții tăi? Ce vrei să comunice website-ul? Cu cât dai mai multe detalii, cu atât demo-urile vor fi mai bune..."
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              onFocus={() => setFocusedField('desc')}
              onBlur={() => setFocusedField(null)}
            />
          </div>

          {/* Submit */}
          <button onClick={handleSubmit} style={{
            background: 'linear-gradient(135deg, #50fa7b, #8be9fd)',
            border: 'none', borderRadius: 10, padding: '15px 0',
            color: '#0d0e12', fontWeight: 700, fontSize: 14,
            cursor: 'pointer',
            fontFamily: "'Space Grotesk', sans-serif",
            boxShadow: '0 0 25px rgba(80,250,123,0.2)',
            transition: 'all 0.2s',
            letterSpacing: '0.02em',
          }}
          onMouseEnter={e => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 8px 30px rgba(80,250,123,0.35)'; }}
          onMouseLeave={e => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 0 25px rgba(80,250,123,0.2)'; }}>
            🧛 Generează 3 demo-uri cu Ai →
          </button>

          {/* Footer */}
          <p style={{
            textAlign: 'center', color: '#2a2d3a', fontSize: 10, margin: 0,
            fontFamily: "'JetBrains Mono', monospace",
          }}>
            Powered by 🧄 Ai din Transilvania • Durează ~30 secunde
          </p>
        </div>
      </div>
    </div>
  );
};

export default BusinessForm;
